import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateTemplateGroupAdditionalReceiverRequest,
  CreateTemplateGroupRequest,
  TemplateGroup,
  TemplateGroupAdditionalReceiver,
  UpdateTemplateGroupAdditionalReceiverRequest,
  UpdateTemplateGroupRequest,
} from '@models/notifications';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  predicateForSorting,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { TemplateGroupsService } from './template-groups.service';

type TemplateGroupsState = {
  templateGroups: TemplateGroup[];
  isLoading: boolean;
  templateGroupCreateLoading: boolean;

  selectedTemplateGroup: TemplateGroup | null;
  selectedTemplateGroupLoading: boolean;

  additionalReceivers: TemplateGroupAdditionalReceiver[];
  additionalReceiversLoading: boolean;

  createAdditionalReceiverLoading: boolean;
  selectedAdditionalReceiver: TemplateGroupAdditionalReceiver | null;
  selectedAdditionalReceiverLoading: boolean;
};

const initialState: TemplateGroupsState = {
  templateGroups: [],
  isLoading: false,
  templateGroupCreateLoading: false,

  selectedTemplateGroup: null,
  selectedTemplateGroupLoading: false,

  additionalReceivers: [],
  additionalReceiversLoading: false,

  createAdditionalReceiverLoading: false,
  selectedAdditionalReceiver: null,
  selectedAdditionalReceiverLoading: false,
};

const templateGroupsDictionary: StateDictionary<TemplateGroup> = {
  entityList: 'templateGroups',
  listLoading: 'isLoading',
  selectedEntity: 'selectedTemplateGroup',
  selectedEntityLoading: 'selectedTemplateGroupLoading',
  entityId: 'id',
  createLoading: 'templateGroupCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const TemplateGroupsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('template-groups-store'),

  withComputed(state => ({
    selectedTemplateGroupId: computed(() => state.selectedTemplateGroup()?.id || null),
    selectedAdditionalReceiverId: computed(() => state.selectedAdditionalReceiver()?.id || null),
  })),

  withMethods((store, templateGroupsService = inject(TemplateGroupsService), router = inject(Router)) => {
    createStateResetHandler<TemplateGroupsState>('/template-groups')(store, initialState);
    createCustomStateSetOnUrlHandler<TemplateGroupsState>('/template-groups', { selectedTemplateGroup: null })(store);
    createCustomStateSetOnRegexHandler<TemplateGroupsState>(new RegExp(`^/notifications/template-groups/${guidRegex}$`))(store, {
      additionalReceivers: [],
    });
    createCustomStateSetOnRegexHandler<TemplateGroupsState>(new RegExp(`^/notifications/template-groups/${guidRegex}/receivers$`))(store, {
      selectedAdditionalReceiver: null,
    });

    const repositoryMethods = {
      getAll: getAll<TemplateGroup>({
        store,
        service: templateGroupsService,
        dictionary: templateGroupsDictionary,
      }),

      getOne: getOne<TemplateGroup>({
        store,
        service: templateGroupsService,
        dictionary: templateGroupsDictionary,
      }),

      add: addEntity<TemplateGroup, CreateTemplateGroupRequest>({
        store,
        service: templateGroupsService,
        dictionary: templateGroupsDictionary,
        onSuccessCallback: entity => {
          router.navigate(['notifications', 'template-groups', entity.id]);
        },
      }),

      update: updateEntity<TemplateGroup, UpdateTemplateGroupRequest>({
        store,
        service: templateGroupsService,
        dictionary: templateGroupsDictionary,
      }),

      delete: deleteEntity<TemplateGroup>({
        store,
        service: templateGroupsService,
        dictionary: templateGroupsDictionary,
        onSuccessCallback: () => {
          router.navigate(['notifications', 'template-groups']);
        },
      }),

      // AdditionalReceivers methods
      getAllAdditionalReceivers: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { additionalReceiversLoading: true })),
          switchMap(templateGroupId =>
            templateGroupsService.getAllAdditionalReceivers(templateGroupId).pipe(
              tap(receivers => {
                patchState(store, {
                  additionalReceivers: sortAlphabetically(receivers, predicateForSorting('groupName')),
                  additionalReceiversLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { additionalReceiversLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      addAdditionalReceiver: rxMethod<{ templateGroupId: string; request: CreateTemplateGroupAdditionalReceiverRequest }>(
        pipe(
          tap(() => patchState(store, { createAdditionalReceiverLoading: true })),
          switchMap(({ templateGroupId, request }) =>
            templateGroupsService.createAdditionalReceiver(templateGroupId, request).pipe(
              tap(receiver => {
                patchState(store, state => ({
                  additionalReceivers: sortAlphabetically([...state.additionalReceivers, receiver], predicateForSorting('groupName')),
                  selectedAdditionalReceiver: receiver,
                  createAdditionalReceiverLoading: false,
                }));
                router.navigate(['notifications', 'template-groups', templateGroupId, 'receivers', receiver.id, 'details']);
              }),
              catchError(() => {
                patchState(store, { createAdditionalReceiverLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateAdditionalReceiver: rxMethod<{
        templateGroupId: string;
        receiverId: string;
        request: UpdateTemplateGroupAdditionalReceiverRequest;
      }>(
        pipe(
          tap(() => patchState(store, { selectedAdditionalReceiverLoading: true })),
          switchMap(({ templateGroupId, receiverId, request }) =>
            templateGroupsService.updateAdditionalReceiver(templateGroupId, receiverId, request).pipe(
              tap(additionalReceiver => {
                patchState(store, state => ({
                  additionalReceivers: sortAlphabetically(
                    findAndReplace<TemplateGroupAdditionalReceiver>(
                      state.additionalReceivers,
                      additionalReceiver,
                      item => item.id === additionalReceiver.id,
                    ),
                    predicateForSorting('groupName'),
                  ),
                  selectedAdditionalReceiver: additionalReceiver,
                  selectedAdditionalReceiverLoading: false,
                }));
              }),
              catchError(() => {
                patchState(store, { selectedAdditionalReceiverLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteAdditionalReceiver: rxMethod<{ templateGroupId: string; receiverId: string }>(
        pipe(
          tap(() => patchState(store, { selectedAdditionalReceiverLoading: true })),
          switchMap(({ templateGroupId, receiverId }) =>
            templateGroupsService.deleteAdditionalReceiver(templateGroupId, receiverId).pipe(
              tap(() => {
                const currentReceivers = store.additionalReceivers();
                patchState(store, {
                  additionalReceivers: currentReceivers.filter(r => r.id !== receiverId),
                  selectedAdditionalReceiver: null,
                  selectedAdditionalReceiverLoading: false,
                });
                router.navigate(['notifications', 'template-groups', templateGroupId, 'receivers']);
              }),
              catchError(() => {
                patchState(store, { selectedAdditionalReceiverLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      selectAdditionalReceiver: rxMethod<{ templateGroupId: string; receiverId: string }>(
        pipe(
          tap(() => {
            patchState(store, { selectedAdditionalReceiverLoading: true });
          }),
          switchMap(({ templateGroupId, receiverId }) =>
            templateGroupsService.getOneAdditionalReceiver(templateGroupId, receiverId).pipe(
              tap(receiver =>
                patchState(store, {
                  selectedAdditionalReceiverLoading: false,
                  selectedAdditionalReceiver: receiver,
                }),
              ),
              catchError(() => {
                patchState(store, { selectedAdditionalReceiverLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToTemplateGroupActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'template-groups', id]);

        if (!result) return false;

        if (!store.selectedTemplateGroupLoading()) repositoryMethods.getOne(id);

        return true;
      },

      goToListPage: (): void => {
        patchState(store, {
          selectedTemplateGroup: null,
        });
        router.navigate(['notifications', 'template-groups']);
      },

      goToAdditionalReceiverActions: async (templateGroupId: string, receiverId: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'template-groups', templateGroupId, 'receivers', receiverId]);

        if (!result) return false;

        if (!store.selectedAdditionalReceiverLoading()) repositoryMethods.selectAdditionalReceiver({ templateGroupId, receiverId });

        return true;
      },

      goToAdditionalReceiverListPage: (templateGroupId: string): void => {
        patchState(store, {
          selectedAdditionalReceiver: null,
        });
        router.navigate(['notifications', 'template-groups', templateGroupId, 'receivers']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface TemplateGroupsStoreType {
  // State Signals
  readonly templateGroups: Signal<TemplateGroup[]>;
  readonly isLoading: Signal<boolean>;
  readonly templateGroupCreateLoading: Signal<boolean>;
  readonly selectedTemplateGroup: Signal<TemplateGroup | null>;
  readonly selectedTemplateGroupLoading: Signal<boolean>;
  readonly additionalReceivers: Signal<TemplateGroupAdditionalReceiver[]>;
  readonly additionalReceiversLoading: Signal<boolean>;
  readonly createAdditionalReceiverLoading: Signal<boolean>;
  readonly selectedAdditionalReceiver: Signal<TemplateGroupAdditionalReceiver | null>;
  readonly selectedAdditionalReceiverLoading: Signal<boolean>;

  // Computed Signals
  readonly selectedTemplateGroupId: Signal<string | null>;
  readonly selectedAdditionalReceiverId: Signal<string | null>;

  // Navigation Methods
  goToTemplateGroupActions: (id: string) => Promise<boolean>;
  goToListPage: () => void;
  goToAdditionalReceiverActions: (templateGroupId: string, receiverId: string) => Promise<boolean>;
  goToAdditionalReceiverListPage: (templateGroupId: string) => void;

  // Template Groups Methods
  getAll: () => void;
  getOne: (id: string) => void;
  add: (request: CreateTemplateGroupRequest) => void;
  update: (input: { id: string; dto: UpdateTemplateGroupRequest }) => void;
  delete: (id: string) => void;

  // Additional Receiver Methods
  getAllAdditionalReceivers: (templateGroupId: string) => void;
  addAdditionalReceiver: (params: { templateGroupId: string; request: CreateTemplateGroupAdditionalReceiverRequest }) => void;
  updateAdditionalReceiver: (params: {
    templateGroupId: string;
    receiverId: string;
    request: UpdateTemplateGroupAdditionalReceiverRequest;
  }) => void;
  deleteAdditionalReceiver: (params: { templateGroupId: string; receiverId: string }) => void;
  selectAdditionalReceiver: (params: { templateGroupId: string; receiverId: string }) => void;
}
